import React, { useEffect, useCallback, useState, lazy, Suspense, useRef } from 'react';
import {
    registerCodeActivationListener,
    unregisterCodeActivationListener,
    openCodeActivationModal,
    CodeActivationTriggerCallback,
} from '@fiverr-private/code_activation_triggers';
import { globalBigQueryReporter, globalGrafanaReporter, globalMixpanelReporter } from '../../events';
import { StatusMessage } from '../../components';
import { CodeActivationProps } from '../../types';
import { VerificationType } from '../../constants';
import { useResendWithCooldown } from '../../components/CodeActivationStep/hooks/useResendWithCooldown';
import { ResendCooldownProvider } from '../../components/shared/ResendCooldownContext';

interface UserActivationProps {
    email: string;
    username: string;
    isPro?: boolean;
    showCodeActivationMessage: boolean;
}

const CodeActivationModalLazy = lazy(() =>
    import(/* webpackChunkName: "code_activation-CodeActivationModal" */ '../../components/CodeActivationModal').then(
        (module) => ({
            default: module.CodeActivationModal,
        })
    )
);

const defaultParams: Partial<CodeActivationProps> = {
    isClosable: true,
    isSkippable: true,
    shouldSendActivationCodeOnOpen: false,
    variant: 'default',
};

const UserActivationContent = ({ email, username, showCodeActivationMessage, isPro = false }: UserActivationProps) => {
    const [params, setParams] = useState<CodeActivationProps>({
        email,
        username,
        isPro,
        ...defaultParams,
    });
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [renderMessage, setRenderMessage] = useState(showCodeActivationMessage);
    const [renderModal, setRenderModal] = useState(false);
    const [pendingVerifications, setPendingVerifications] = useState<VerificationType[] | undefined>(undefined);
    const { isResending, resend: resendCode } = useResendWithCooldown();

    const ref = useRef<CodeActivationTriggerCallback | null>(null);

    useEffect(() => {
        ref.current = (data) => {
            setRenderModal(true);
            const activationStatus = (data as Record<string, unknown> | undefined)?.activationStatus as
                | { pending_verifications?: VerificationType[] }
                | undefined;
            setPendingVerifications(activationStatus?.pending_verifications);
            setParams((prev) => ({
                ...prev,
                shouldSendActivationCodeOnOpen:
                    data?.shouldSendActivationCodeOnOpen ?? defaultParams.shouldSendActivationCodeOnOpen,
                variant: data?.variant ?? defaultParams.variant,
                onClose: data?.onClose ?? defaultParams.onClose,
                onSkip: data?.onSkip ?? defaultParams.onSkip,
                onSuccess: data?.onSuccess ?? defaultParams.onSuccess,
                onError: data?.onError ?? defaultParams.onError,
                isSkippable: data?.isSkippable ?? defaultParams.isSkippable,
                isClosable: data?.isClosable ?? defaultParams.isClosable,
                phoneVerificationConfig: data?.phoneVerificationConfig ?? defaultParams.phoneVerificationConfig,
                sourceComponent: data?.sourceComponent ?? defaultParams.sourceComponent,
            }));
            setModalIsOpen(true);
        };

        registerCodeActivationListener(ref.current);

        return () => {
            if (ref.current) {
                unregisterCodeActivationListener(ref.current);
            }
        };
    }, []);

    const handleSubmit = useCallback(async () => {
        try {
            setRenderModal(true);

            const result = await resendCode(email);

            if (result && !result.success) {
                throw new Error(`Resend Activation Email Error: ${result.translatedError ?? 'unspecified'}`);
            }

            await openCodeActivationModal({
                shouldSendActivationCodeOnOpen: false,
                variant: 'default',
                onClose: () => null,
                onSuccess: () => null,
                onError: () => null,
                onSkip: () => null,
            });

            globalBigQueryReporter.send('activation_banner_resend');
            globalMixpanelReporter.send('activation banner - resend email');
            globalGrafanaReporter.count('activation_banner.action.resend');
        } catch {
            globalBigQueryReporter.send('activation_banner_resend_code_error');
            globalMixpanelReporter.send('activation banner - resend code error');
            globalGrafanaReporter.count('activation_banner.action.resend_error');
        }
    }, [email, resendCode]);

    const handleSuccess = () => {
        setModalIsOpen(false);
        setRenderMessage(false);
        setRenderModal(false);
        params.onSuccess?.();
    };

    const handleClose = () => {
        setModalIsOpen(false);
        params.onClose?.();
    };

    const handleSkip = () => {
        setModalIsOpen(false);
        params.onSkip?.();
    };

    const handleError = (error) => {
        params.onError?.(error);
    };

    return (
        <>
            {renderMessage && <StatusMessage isLoading={isResending} handleSubmit={handleSubmit} />}
            {renderModal && (
                <Suspense fallback={null}>
                    <CodeActivationModalLazy
                        {...params}
                        isOpen={modalIsOpen}
                        isSkippable={params.isSkippable}
                        isClosable={params.isClosable}
                        onClose={handleClose}
                        onSkip={handleSkip}
                        onError={handleError}
                        onSuccess={handleSuccess}
                        pendingVerifications={pendingVerifications}
                        phoneVerificationConfig={params.phoneVerificationConfig}
                        sourceComponent={params.sourceComponent}
                    />
                </Suspense>
            )}
        </>
    );
};

export const UserActivation = (props: UserActivationProps) => (
    <ResendCooldownProvider>
        <UserActivationContent {...props} />
    </ResendCooldownProvider>
);
